import React, { useEffect, useMemo } from 'react'
import { Box } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import qs from 'query-string'
import { selectCampaign, selectRequestsAreLoading, makeSelectCampaignPerformanceData } from 'state'
import {
    getCampaignPerformance,
    clearCampaignPerformance,
    getCampaign,
    exportCampaignPerformance,
} from 'state/campaigns'
import { getTasksList, clearTasksList, selectTasksPayoutColumns, selectTasks } from 'state/tasks'
import { STATE_KEYS } from 'state/requests'
import { MAX_PAGE_SIZE, selectActivePageNum, selectSearchTerm } from 'state/review'
import { theme } from 'theme'
import { selectConstants, selectCountrySetting } from 'state/constants'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { PageHeader } from '.'
import ExportModal from './Review/common/ExportModal'
import { requestProfileOptions } from 'state/constants'
import { TableViewType } from 'components/Shared/MuiTable'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import _ from 'lodash'
import { PLATFORM_ENUM } from 'services/api/constants'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { useShowPrivateCampaignBudget } from 'hooks/permissions'
import { setTeamCountryByCampaign } from 'state/filter'
import { EarningAdjustmentCell } from 'components/Shared/MuiTable/Cell/EarningAdjustmentCell'
import PayoutStatusCell from 'components/Shared/MuiTable/Cell/PayoutStatusCell'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

interface ViewPerformanceProps {
    location: any
}

const ViewPerformance: React.FC<ViewPerformanceProps> = ({ location }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const params = qs.parse(location.search)

    const { campaign: campaignUuid } = params

    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const countrySetting = useSelector(selectCountrySetting(campaign && campaign.country))
    const currencySign = countrySetting ? countrySetting.currency.sign : '$'
    const tasks = useSelector(selectTasks)
    const { campaign: campaignSetting } = useSelector(selectConstants)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])
    const hasShowPrivateCampaignBudget = useShowPrivateCampaignBudget(campaign?.is_private)

    const isLoading = useSelector(
        selectRequestsAreLoading([STATE_KEYS.Campaign, STATE_KEYS.TasksList, STATE_KEYS.CampaignPerformance]),
    )
    const isExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportPerformance]))

    const extraColumns = useSelector(selectTasksPayoutColumns(!hasShowPrivateCampaignBudget, currencySign))
    const selectCampaignPerformanceData = useMemo(makeSelectCampaignPerformanceData, [])
    const data = useSelector(selectCampaignPerformanceData)
    const activePageNum = useSelector(selectActivePageNum)
    const searchTerm = useSelector(selectSearchTerm)
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return campaignPlatforms.includes(platform)
    }

    const handleClickExport = () => {
        if (campaign && campaign.uuid) dispatch(exportCampaignPerformance({ uuid: campaign.uuid }))
    }

    useEffect(() => {
        const offset: number = activePageNum * MAX_PAGE_SIZE
        if (campaignUuid && !isLoading) {
            dispatch(
                getCampaign({
                    uuid: campaignUuid as string,
                    onSuccess: campaign => {
                        dispatch(setTeamCountryByCampaign(campaign.uuid))
                    },
                }),
            )
            dispatch(getCampaignPerformance({ uuid: campaignUuid as string, offset, keywords: searchTerm }))
            dispatch(getTasksList({ campaignUuid: campaignUuid as string }))
        }
        return () => {
            dispatch(clearTasksList())
            dispatch(clearCampaignPerformance())
        }
    }, [campaignUuid, activePageNum, searchTerm])

    useEffect(() => {
        if (campaign) dispatch(requestProfileOptions({ country: campaign.country }))
    }, [campaign])

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'profile',
                type: 'string',
                headerName: newT('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: newT('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: newT('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: newT('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: newT('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(PLATFORM_ENUM.IG)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: newT('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: newT('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: newT('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: newT('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: newT('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: newT('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: newT('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: newT('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                                  headerName: newT('submission.table.XHSFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: newT('submission.table.XHSUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            ...(campaignPlatforms.includes(PLATFORM_ENUM.IG)
                ? [
                      {
                          field: 'instagramFollowerCount',
                          headerName: newT('submission.table.instagramFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 255,
                              title: newT('submission.table.followerCountLabel'),
                              description: newT(
                                  'submission.table.followerCountTooltip',
                                  'The number Partiposters entered when submitting engagement result for the campaign',
                              ),
                              options: { mr: 0 },
                          }),
                          width: 255,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.FB)
                ? [
                      {
                          field: 'facebookFollowerCount',
                          headerName: newT('submission.table.facebookFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 253,
                              title: newT('submission.table.followerCountLabel'),
                              description: newT(
                                  'submission.table.followerCountTooltip',
                                  'The number Partiposters entered when submitting engagement result for the campaign',
                              ),
                              options: { mr: 0 },
                          }),
                          width: 253,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.TIKTOK)
                ? [
                      {
                          field: 'tiktokFollowerCount',
                          headerName: newT('submission.table.tikTokFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 277,
                              title: newT('submission.table.followerCountLabel'),
                              description: newT(
                                  'submission.table.followerCountTooltip',
                                  'The number Partiposters entered when submitting engagement result for the campaign',
                              ),
                              options: { mr: 0 },
                          }),
                          width: 277,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.TWITTER)
                ? [
                      {
                          field: 'twitterFollowerCount',
                          headerName: newT('submission.table.twitterFollowersLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 285,
                              title: newT('submission.table.followerCountLabel'),
                              description: newT(
                                  'submission.table.followerCountTooltip',
                                  'The number Partiposters entered when submitting engagement result for the campaign',
                              ),
                              options: { mr: 0 },
                          }),
                          width: 285,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...extraColumns,
            ...(hasShowPrivateCampaignBudget
                ? [
                      {
                          field: 'totalEarnings',
                          headerName: newT('submission.table.totalEarningsLabel'),
                          width: 150,
                          renderCell: (props: GridRenderCellParams) => <EarningAdjustmentCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'paymentStatus',
                headerName: newT('submission.table.statusLabel'),
                width: 190,
                renderCell: (props: GridRenderCellParams) => <PayoutStatusCell {...props} />,
                sortable: false,
                filterable: false,
            },
        ],
        [extraColumns, campaignPlatforms],
    )

    interface Row {
        [key: string]:
            | {
                  value: number
                  taskUuid: string
                  performanceKey: string
              }
            | { value: string }
    }

    const taskNameRows = useMemo(
        () =>
            tasks.map((task: any) => {
                const taskNameRow: Row = {}
                taskNameRow[task.uuid + '_taskName'] = { value: task.title }
                return taskNameRow
            }),
        [tasks],
    )

    const getExtraRows = (taskPerformances: any) => {
        const perfArr = Object.keys(taskPerformances).map((taskUuid: string) => {
            const performanceData = taskPerformances[taskUuid]
            const rowsArr = Object.keys(performanceData).map((performanceKey: string) => {
                const rowObj: Row = {}
                const rowKey = taskUuid + '_' + performanceKey
                rowObj[rowKey] = {
                    value: performanceData[performanceKey].count,
                    taskUuid: taskUuid,
                    performanceKey: performanceKey,
                }

                return rowObj
            })
            return rowsArr.concat(taskNameRows).flat()
        })
        return perfArr.flat()
    }

    const rows = useMemo(
        () =>
            data.map(performance => {
                const { partiposter_profile } = performance
                const { social_media_accounts: socialMediaAccounts } = partiposter_profile
                const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
                const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
                const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
                const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
                const xiaohongshuProfile = socialMediaAccounts.find(
                    account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
                )
                const extraRows = getExtraRows(performance.taskPerformances)

                return Object.assign(
                    {
                        id: performance.uuid,
                        profile: { profile: partiposter_profile },
                        socialMediaInfo: {
                            profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
                        },
                        instagramFollowerCount: { value: performance.instagram_follower_count },
                        facebookFollowerCount: { value: performance.facebook_follower_count },
                        tiktokFollowerCount: { value: performance.tiktok_follower_count },
                        twitterFollowerCount: { value: performance.twitter_follower_count },
                        youtubeFollowerCount: { value: performance.youtube_follower_count },
                        xiaohongshuFollowerCount: { value: performance.xiaohongshu_follower_count },
                        totalEarnings: {
                            earning: performance.totalEarnings,
                            earning_before_adjustment: performance.total_earnings_without_adjustment,
                            adjustment_reason:
                                performance.earnings_adjustment_reason === 'others'
                                    ? performance.earnings_adjustment_custom_reason
                                    : newT(
                                          campaignSetting?.submission_earnings_adjustment_reasons?.find(
                                              reason => reason.id === performance.earnings_adjustment_reason,
                                          )?.label || '',
                                      ),
                        },
                        paymentStatus: {
                            paymentStatus: performance.paymentStatus,

                            isPaidOutsideApp: performance.is_paid_outside_app,
                            paidOutsideAppReason:
                                performance.paid_outside_app_reason === 'others'
                                    ? performance.paid_outside_app_custom_reason
                                    : newT(
                                          campaignSetting?.submission_paid_outside_app_reasons?.find(
                                              reason => reason.id === performance.paid_outside_app_reason,
                                          )?.label || '',
                                      ),
                            status: {
                                incomplete: newT('statusLabel.incompleteSubmission'),
                                rejected: newT('statusLabel.rejected'),
                                paid: newT('statusLabel.paid'),
                                closed: newT('statusLabel.closed'),
                                paidOutsideApp: newT('statusLabel.paidOutsideApp'),
                                awaiting: newT('statusLabel.awaitingPayout'),
                            },
                        },
                        uuid: performance.uuid,
                    },
                    ...extraRows,
                )
            }),
        [campaignUuid, data, campaign],
    )

    const enableExport = rows.length > 0

    if (!campaignUuid) return <Redirect to="/submissions" />

    return (
        <>
            <Box mx={theme.spacing.xs} mt={theme.spacing.xs}>
                {campaign ? <PageHeader title={campaign.title} subTitle={newT('button.trackPerformance')} /> : null}
                <PartiTable
                    rowHeight={84}
                    columns={columns}
                    rows={rows}
                    isLoading={isLoading}
                    viewType={TableViewType.PERFORMANCE}
                    hideToolbar={false}
                    enableExport={enableExport}
                />
            </Box>
            <ExportModal
                tableTitle={newT('submission.exportList.partipostPerformanceTableTitle')}
                isRequesting={isExportRequesting}
                onClickExport={handleClickExport}
            />
        </>
    )
}

export default ViewPerformance
